/**
 * masteryLabel
 * ─────────────────────────────────────────────────────────────
 * Maps a BKT mastery_score in [0, 1] to a qualitative PT-MZ label
 * + a Tailwind text color, so student-facing surfaces can describe
 * skill level WITHOUT leaking the raw Bayesian percentage.
 *
 * Why we hide the raw %:
 *   The number is a Bayesian Knowledge Tracing estimate computed
 *   in services/mastery.ts (backend). It accounts for difficulty,
 *   speed (slow-correct gets less credit), and recency. Students
 *   read "31%" as "31% correct out of attempted" and conclude
 *   "I'm doing badly" — but it's not that. The label is the
 *   honest signal; the raw % was leaking the underlying scale.
 *
 * Surfaces that USE the qualitative label:
 *   - SubjectSelectPage mastery bar + weakest-subject banner
 *   - QuestionPage post-attempt feedback chip
 *
 * Surfaces that KEEP the raw % (deliberate):
 *   - ParentDashboardPage — parents are an analytical audience and
 *     specifically asked for the precise number.
 *   - Placement results (PlacementPage) — output of an actual
 *     assessment session, not a long-term BKT estimate; the
 *     number is more meaningful in that context.
 *   - UniversityRecommendationsPage tooltip — title= attribute,
 *     hidden by default, only seen on hover.
 *
 * Threshold calibration is unchanged from the original
 * implementation in SubjectSelectPage:
 *
 *     score < 0.25  →  Por começar
 *   0.25 ≤ score < 0.50  →  Em progresso
 *   0.50 ≤ score < 0.75  →  A melhorar
 *   0.75 ≤ score        →  Dominado
 */

export interface MasteryLabel {
  text:  string
  /** Tailwind class string (e.g. 'text-green-600'). Caller's responsibility
   *  to combine with whatever sizing classes their component uses. */
  color: string
}

export function masteryLabel(score: number): MasteryLabel {
  if (score >= 0.75) return { text: 'Dominado',     color: 'text-green-600' }
  if (score >= 0.50) return { text: 'A melhorar',   color: 'text-amber-600' }
  if (score >= 0.25) return { text: 'Em progresso', color: 'text-orange-500' }
  return                    { text: 'Por começar',  color: 'text-gray-400'  }
}
